'use client';

import React, { useState } from 'react';
import { useCart } from '@/app/lib/papa/cardapio/cart-context';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    DeleteOutline as DeleteIcon,
    ShoppingBagOutlined as ShoppingBagIcon,
    ArrowForward as ArrowForwardIcon,
    EditNote as EditNoteIcon,
    ShoppingCartCheckout as CheckoutIcon
} from '@mui/icons-material';

function formatCurrency(value: number) {
    return 'R$ ' + value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface CartDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
    const router = useRouter();
    const { cart, removeFromCart, updateQuantity, updateObservacao, totalItems, totalPrice } = useCart();
    const [editingObsKey, setEditingObsKey] = useState<string | null>(null);
    const [tempObs, setTempObs] = useState<string>('');

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] overflow-hidden">
            {/* Backdrop com blur suave */}
            <div
                onClick={onClose}
                className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
            />

            {/* Container do Drawer (Slide-over do lado direito no desktop / painel inferior em telas menores) */}
            <div className="fixed inset-y-0 right-0 max-w-full flex pl-0 sm:pl-10 z-[10000]">
                <div className="w-screen max-w-md bg-white dark:bg-slate-900 shadow-2xl flex flex-col justify-between border-l border-slate-200 dark:border-slate-800 animate-in slide-in-from-right duration-300 h-full">
                    
                    {/* Header do Drawer */}
                    <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/40">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-xl bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400">
                                <ShoppingBagIcon fontSize="small" />
                            </div>
                            <div>
                                <h2 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <span>Seu Carrinho</span>
                                    <span className="px-2 py-0.5 rounded-full text-xs font-extrabold bg-red-500 text-white">
                                        {totalItems}
                                    </span>
                                </h2>
                                <p className="text-[11px] text-slate-400 font-medium">
                                    {totalItems === 1 ? '1 item adicionado' : `${totalItems} itens adicionados`}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            type="button"
                            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        >
                            <CloseIcon fontSize="small" />
                        </button>
                    </div>

                    {/* Conteúdo com scroll dos itens */}
                    <div className="flex-1 overflow-y-auto px-5 py-4 divide-y divide-slate-100 dark:divide-slate-800/80">
                        {cart.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center py-12 px-4 space-y-4">
                                <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400">
                                    <ShoppingBagIcon fontSize="large" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-slate-800 dark:text-white">Seu carrinho está vazio</h3>
                                    <p className="text-xs text-slate-400 mt-1 max-w-xs">
                                        Escolha seus produtos favoritos no cardápio e adicione aqui!
                                    </p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                                >
                                    Ver Cardápio
                                </button>
                            </div>
                        ) : (
                            cart.map((item) => {
                                const itemKey = `${item.produto.id}_${item.tamanho.id}`;
                                const precoUnitario = item.tamanho.valor_desconto && item.tamanho.valor_desconto > 0 && item.tamanho.valor_desconto !== item.tamanho.valor
                                    ? item.tamanho.valor_desconto
                                    : item.tamanho.valor;
                                const totalItemPreco = precoUnitario * item.quantity;
                                const maxEstoque = (item.tamanho.gerenciar_estoque !== false && item.tamanho.quantidade_atual !== undefined)
                                    ? Math.max(0, item.tamanho.quantidade_atual)
                                    : 999;
                                const isMax = item.quantity >= maxEstoque;

                                return (
                                    <div key={itemKey} className="py-4 first:pt-0 last:pb-0 space-y-2.5">
                                        <div className="flex items-start gap-3">
                                            {item.produto.foto_url && (
                                                <Image
                                                    unoptimized
                                                    src={item.produto.foto_url}
                                                    alt={item.produto.titulo}
                                                    width={56}
                                                    height={56}
                                                    className="w-14 h-14 object-cover rounded-xl border border-slate-200 dark:border-slate-700 shrink-0 bg-slate-50"
                                                />
                                            )}

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-1">
                                                    <h3 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                                        {item.produto.titulo}
                                                    </h3>
                                                    <button
                                                        onClick={() => removeFromCart(item.produto.id, item.tamanho.id)}
                                                        className="text-slate-400 hover:text-red-500 transition-colors p-0.5 cursor-pointer shrink-0"
                                                        title="Remover item"
                                                    >
                                                        <DeleteIcon fontSize="inherit" className="text-base" />
                                                    </button>
                                                </div>

                                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                                                    <span className="font-semibold bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[10px]">
                                                        {item.tamanho.nome}
                                                    </span>
                                                    <span>•</span>
                                                    <span>{formatCurrency(precoUnitario)}</span>
                                                </div>

                                                <div className="flex items-center justify-between mt-2">
                                                    {/* Controles de Quantidade */}
                                                    <div className="inline-flex items-center border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/80">
                                                        <button
                                                            onClick={() => updateQuantity(item.produto.id, item.tamanho.id, item.quantity - 1)}
                                                            className="w-6 h-6 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 transition-colors cursor-pointer"
                                                        >
                                                            <RemoveIcon fontSize="inherit" className="text-xs" />
                                                        </button>
                                                        <span className="w-7 text-center text-xs font-bold text-slate-800 dark:text-white">
                                                            {item.quantity}
                                                        </span>
                                                        <button
                                                            disabled={isMax}
                                                            onClick={() => updateQuantity(item.produto.id, item.tamanho.id, item.quantity + 1)}
                                                            className={`w-6 h-6 flex items-center justify-center transition-colors ${
                                                                isMax
                                                                    ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                                                                    : 'text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 cursor-pointer'
                                                            }`}
                                                        >
                                                            <AddIcon fontSize="inherit" className="text-xs" />
                                                        </button>
                                                    </div>

                                                    {/* Preço Total do Item */}
                                                    <span className="text-xs font-bold text-slate-900 dark:text-white font-mono">
                                                        {formatCurrency(totalItemPreco)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Observação por Item */}
                                        <div className="pt-1">
                                            {editingObsKey === itemKey ? (
                                                <div className="flex items-center gap-1.5 w-full">
                                                    <input
                                                        type="text"
                                                        value={tempObs}
                                                        onChange={(e) => setTempObs(e.target.value)}
                                                        placeholder="Ex: sem cebola, ponto..."
                                                        className="flex-1 text-[11px] px-2 py-1 rounded-md border border-red-300 dark:border-red-900 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-hidden focus:ring-1 focus:ring-red-500"
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            updateObservacao(item.produto.id, item.tamanho.id, tempObs.trim());
                                                            setEditingObsKey(null);
                                                        }}
                                                        className="text-[11px] px-2 py-1 bg-red-600 hover:bg-red-700 text-white font-bold rounded-md transition-colors cursor-pointer"
                                                    >
                                                        Salvar
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingObsKey(null)}
                                                        className="text-[11px] px-1.5 py-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            ) : item.observacao ? (
                                                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 text-amber-800 dark:text-amber-300 text-[11px] max-w-full">
                                                    <span className="font-bold">Obs:</span>
                                                    <span className="truncate">{item.observacao}</span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingObsKey(itemKey);
                                                            setTempObs(item.observacao || '');
                                                        }}
                                                        className="text-amber-600 dark:text-amber-400 hover:underline font-bold text-[10px] ml-1 cursor-pointer"
                                                    >
                                                        Editar
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => {
                                                        setEditingObsKey(itemKey);
                                                        setTempObs('');
                                                    }}
                                                    className="text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors cursor-pointer"
                                                >
                                                    <EditNoteIcon fontSize="inherit" className="text-sm" />
                                                    <span>Adicionar observação</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer com Subtotal e Ações Rápidas */}
                    {cart.length > 0 && (
                        <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Subtotal</span>
                                <span className="text-base font-extrabold text-slate-900 dark:text-white font-mono">
                                    {formatCurrency(totalPrice)}
                                </span>
                            </div>

                            <div className="space-y-2 pt-1">
                                {/* Botão Principal: Direto para Checkout */}
                                <button
                                    onClick={() => {
                                        onClose();
                                        router.push('/cardapio/checkout');
                                    }}
                                    className="w-full py-3 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                                >
                                    <CheckoutIcon fontSize="small" />
                                    <span>Avançar para o Pagamento</span>
                                    <ArrowForwardIcon fontSize="small" />
                                </button>

                                {/* Botão Secundário: Ver Carrinho Completo */}
                                <Link
                                    href="/cardapio/carrinho"
                                    onClick={onClose}
                                    className="w-full py-2.5 px-4 rounded-xl bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 font-semibold text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer text-center block"
                                >
                                    <span>Ver Carrinho Completo (Aplicar Cupons)</span>
                                </Link>

                                <button
                                    onClick={onClose}
                                    className="w-full py-1.5 text-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-[11px] font-medium transition-colors cursor-pointer"
                                >
                                    Continuar comprando
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
